import { ElasticsearchResponse } from '../types/chat';
import { CommandLogElasticsearchResponse } from '../types/command';
import { CompensationLogResponse } from '../types/compensation';
import { PlayerSpawnElasticsearchResponse } from '../types/playerSpawn';
import { ShopChangelogResponse } from '../types/shopChangelog';

export const SHOP_CHANGELOG_INDEX = 'quasar-shopdata-changelog';

const ELASTICSEARCH_BASE_URL = import.meta.env.DEV 
  ? '/es' 
  : 'https://es.playcdu.co';

class ElasticsearchService {
  private accessHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (import.meta.env.PROD) {
      headers['CF-Access-Client-Id'] = '224e16ecb833ed9d6de3a805919a4afd.access';
      headers['CF-Access-Client-Secret'] = '22112ca896a66ae006acd8d9e1036b96b054c4a12c735ec53cac34b358aa097d';
    }
    return headers;
  }

  private async search<T>(index: string, query: object): Promise<T> {
    const response = await fetch(`${ELASTICSEARCH_BASE_URL}/${index}/_search`, {
      method: 'POST',
      headers: this.accessHeaders(),
      body: JSON.stringify(query),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Elasticsearch query failed with status ${response.status}: ${errorBody}`);
    }

    return response.json();
  }

  /** Delete one document by id. Resolves false when it was already gone. */
  public async deleteDocument(index: string, docId: string): Promise<boolean> {
    if (!/^[A-Za-z0-9_-]+$/.test(index)) throw new Error(`Refusing to delete from an unexpected index: ${index}`);
    const response = await fetch(`${ELASTICSEARCH_BASE_URL}/${index}/_doc/${encodeURIComponent(docId)}`, { method: 'DELETE', headers: this.accessHeaders() });
    if (response.status === 404) return false;
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Elasticsearch delete failed with status ${response.status}: ${errorBody}`);
    }
    return true;
  }

  /** `_update_by_query`: returns the number of documents updated. */
  public async updateByQuery(index: string, body: object): Promise<number> {
    if (!/^[A-Za-z0-9_-]+$/.test(index)) throw new Error(`Refusing to update an unexpected index: ${index}`);
    const response = await fetch(`${ELASTICSEARCH_BASE_URL}/${index}/_update_by_query?refresh=true&conflicts=proceed`, {
      method: 'POST',
      headers: this.accessHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Elasticsearch update-by-query failed with status ${response.status}: ${errorBody}`);
    }
    const result = (await response.json()) as { updated?: number; failures?: unknown[] };
    if (Array.isArray(result.failures) && result.failures.length > 0) throw new Error(`Elasticsearch update-by-query reported ${result.failures.length} failures`);
    return result.updated ?? 0;
  }

  /**
   * Generic search against an arbitrary index — used by the Event Team command-log
   * clearance runner (the backend generates the query; the browser executes it
   * because the ES cluster sits behind Cloudflare Access).
   */
  public searchIndex<T = unknown>(index: string, query: object): Promise<T> {
    return this.search<T>(index, query);
  }

  public getChatMessages(query: object): Promise<ElasticsearchResponse> {
    return this.search<ElasticsearchResponse>('chat_messages', query);
  }

  public getCommandLogs(query: object): Promise<CommandLogElasticsearchResponse> {
    return this.search<CommandLogElasticsearchResponse>('cmd_logs_v1', query);
  }

  public getPlayerSpawns(query: object): Promise<PlayerSpawnElasticsearchResponse> {
    return this.search<PlayerSpawnElasticsearchResponse>('player_spawns', query);
  }

  public getCompensationLogs(query: object): Promise<CompensationLogResponse> {
    return this.search<CompensationLogResponse>('compensation_logs', query);
  }

  public getShopChangelog(query: object): Promise<ShopChangelogResponse> {
    return this.search<ShopChangelogResponse>(SHOP_CHANGELOG_INDEX, query);
  }

  public searchPlayers(query: string): Promise<any> {
    const escapeQuery = (str: string) => {
      // Escape special characters for Lucene query string syntax, but allow wildcards.
      return str.replace(/[+\-=&|!(){}\[\]^"~?:\\/<>]/g, '\\$&');
    }
    const escapedQuery = escapeQuery(query);

    const isIpLike = /^[0-9\.]{7,}$/.test(query) && query.includes('.');
    const finalQuery = isIpLike ? `${escapedQuery}*` : `*${escapedQuery}*`;

    const searchQuery = {
      size: 10,
      query: {
        query_string: {
          query: finalQuery,
          fields: [
            "lastKnownName^3",
            "nickname^3",
            "uuid.search^2",
            "lastKnownIp.search^5"
          ],
          analyze_wildcard: true,
          ...(!isIpLike && { fuzziness: "AUTO" })
        }
      }
    };
    return this.search<any>('cdu-players-v1', searchQuery);
  }

  public updatePlayerName(docId: string, newName: string): Promise<any> {
    const updateQuery = {
      doc: {
        lastKnownName: newName
      }
    };
    
    return fetch(`${ELASTICSEARCH_BASE_URL}/cdu-players-v1/_update/${docId}`, {
      method: 'POST',
      headers: this.accessHeaders(),
      body: JSON.stringify(updateQuery),
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(errorBody => {
          throw new Error(`Elasticsearch update failed with status ${response.status}: ${errorBody}`);
        });
      }
      return response.json();
    });
  }
}

export const elasticsearchService = new ElasticsearchService();
